import React from 'react';
import axios from 'axios';
import {
  AtomicBlockUtils,
  ContentBlock,
  ContentState,
  Editor,
  EditorState,
  RichUtils,
  convertFromHTML,
  getDefaultKeyBinding
} from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import 'draft-js/dist/Draft.css';

const workApiBaseUrl = 'https://lrfiosxbs6.execute-api.ap-northeast-2.amazonaws.com';
const workImageBaseUrl = 'https://d1aummmer0n4kg.cloudfront.net/media/';

const inlineControls = [
  {
    label: <span className="admin-editor-label admin-editor-label-bold">B</span>,
    ariaLabel: 'Bold',
    style: 'BOLD'
  },
  {
    label: <span className="admin-editor-label admin-editor-label-italic">I</span>,
    ariaLabel: 'Italic',
    style: 'ITALIC'
  },
  {
    label: <span className="admin-editor-label admin-editor-label-underline">U</span>,
    ariaLabel: 'Underline',
    style: 'UNDERLINE'
  }
];

const blockControls = [
  {
    label: <span className="admin-editor-label admin-editor-label-h1">H1</span>,
    ariaLabel: 'Heading 1',
    style: 'header-one'
  },
  {
    label: <span className="admin-editor-label admin-editor-label-h2">H2</span>,
    ariaLabel: 'Heading 2',
    style: 'header-two'
  },
  {
    label: <span className="admin-editor-label admin-editor-label-quote">"</span>,
    ariaLabel: 'Quote',
    style: 'blockquote'
  },
  {
    label: <span className="admin-editor-label admin-editor-label-list">• List</span>,
    ariaLabel: 'Bulleted list',
    style: 'unordered-list-item'
  },
  {
    label: <span className="admin-editor-label admin-editor-label-list">1. List</span>,
    ariaLabel: 'Numbered list',
    style: 'ordered-list-item'
  }
];

function EditorToolbarButton({ active, ariaLabel, label, onMouseDown }) {
  return (
    <button
      type="button"
      className={`admin-editor-button${active ? ' active' : ''}`}
      onMouseDown={onMouseDown}
      aria-label={ariaLabel || (typeof label === 'string' ? label : undefined)}
    >
      {label}
    </button>
  );
}

function WorkContentImage(props) {
  const { block, contentState } = props;
  const entity = contentState.getEntity(block.getEntityAt(0));
  const { src, alt } = entity.getData();

  return <img className="admin-editor-inline-image" src={src} alt={alt || ''} />;
}

function ImageToolbarIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect x="3.5" y="5.5" width="17" height="13" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="9" cy="10" r="1.7" fill="currentColor" />
      <path d="M6.5 16L11 11.5L14 14.5L16 12.5L19 16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

class AdminWorkManager extends React.Component {
  state = {
    mode: 'list',
    page: 1,
    pageSize: 10,
    workItems: [],
    isListLoading: true,
    isSubmitting: false,
    errorMessage: '',
    form: {
      id: null,
      title: '',
      created: '',
      imageFile: null,
      imagePreview: '',
      contentEditorState: EditorState.createEmpty()
    }
  }

  componentDidMount() {
    this.fetchWorkList();
    window.addEventListener('popstate', this.handlePopState);
  }

  componentWillUnmount() {
    window.removeEventListener('popstate', this.handlePopState);

    if (this.errorToastTimer) {
      clearTimeout(this.errorToastTimer);
    }
  }

  handlePopState = (event) => {
    if (event.state && event.state.adminWorkForm) {
      return;
    }

    this.setState({ mode: 'list', errorMessage: '' });
  }

  showErrorToast = (message) => {
    if (this.errorToastTimer) {
      clearTimeout(this.errorToastTimer);
    }

    this.setState({ errorMessage: message });
    this.errorToastTimer = setTimeout(() => {
      this.setState({ errorMessage: '' });
    }, 3000);
  }

  createEditorStateFromHtml = (html) => {
    if (!html) {
      return EditorState.createEmpty();
    }

    const blocksFromHTML = convertFromHTML(html);

    if (blocksFromHTML.contentBlocks && blocksFromHTML.contentBlocks.length > 0) {
      return EditorState.createWithContent(
        ContentState.createFromBlockArray(
          blocksFromHTML.contentBlocks,
          blocksFromHTML.entityMap
        )
      );
    }

    return EditorState.createWithContent(ContentState.createFromText(html));
  }

  fetchWorkList = () => {
    const { page } = this.state;

    this.setState({ isListLoading: true });

    axios.get(`${workApiBaseUrl}/default/works`, {
      params: {
        page
      }
    })
      .then((response) => {
        console.log('Work list response', response.data);
        this.setState({
          page: response.data.page || page,
          pageSize: response.data.page_size || 10,
          workItems: response.data.items || [],
          isListLoading: false
        });
      })
      .catch((error) => {
        console.error('Work list request failed', error);
        this.setState({ isListLoading: false });
      });
  }

  goToPage = (nextPage) => {
    if (nextPage < 1) {
      return;
    }

    this.setState({
      page: nextPage
    }, this.fetchWorkList);
  }

  openCreateForm = () => {
    window.history.pushState({ adminWorkForm: true, mode: 'create' }, '');
    this.setState({
      mode: 'form',
      errorMessage: '',
      form: {
        id: null,
        title: '',
        created: '',
        imageFile: null,
        imagePreview: '',
        contentEditorState: EditorState.createEmpty()
      }
    });
  }

  openEditForm = (item) => {
    window.history.pushState({ adminWorkForm: true, mode: 'edit', id: item.id }, '');
    this.setState({
      mode: 'form',
      errorMessage: '',
      form: {
        id: item.id,
        title: item.title || '',
        created: item.created || '',
        imageFile: null,
        imagePreview: item.thumbnail || item.image ? `${workImageBaseUrl}${(item.thumbnail || item.image).replace(/^\/+/, '')}` : '',
        contentEditorState: this.createEditorStateFromHtml(item.content || '')
      }
    });
  }

  closeForm = () => {
    if (window.history.state && window.history.state.adminWorkForm) {
      window.history.back();
      return;
    }

    this.setState({ mode: 'list' });
  }

  handleFormChange = (e) => {
    const { name, value } = e.target;

    this.setState((prevState) => ({
      form: {
        ...prevState.form,
        [name]: value
      }
    }));
  }

  handleImageChange = (e) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;

    this.setState((prevState) => ({
      form: {
        ...prevState.form,
        imageFile: file,
        imagePreview: file ? URL.createObjectURL(file) : prevState.form.imagePreview
      }
    }));
  }

  handleEditorChange = (contentEditorState) => {
    this.setState((prevState) => ({
      form: {
        ...prevState.form,
        contentEditorState
      }
    }));
  }

  handleEditorKeyCommand = (command, editorState) => {
    const nextState = RichUtils.handleKeyCommand(editorState, command);

    if (nextState) {
      this.handleEditorChange(nextState);
      return 'handled';
    }

    return 'not-handled';
  }

  mapKeyToEditorCommand = (e) => {
    if (e.key === 'Tab') {
      return null;
    }

    return getDefaultKeyBinding(e);
  }

  toggleInlineStyle = (style) => (e) => {
    e.preventDefault();
    this.handleEditorChange(RichUtils.toggleInlineStyle(this.state.form.contentEditorState, style));
  }

  toggleBlockType = (style) => (e) => {
    e.preventDefault();
    this.handleEditorChange(RichUtils.toggleBlockType(this.state.form.contentEditorState, style));
  }

  focusEditor = () => {
    if (this.editorRef) {
      this.editorRef.focus();
    }
  }

  focusContentImageInput = (e) => {
    e.preventDefault();

    if (this.contentImageInputRef) {
      this.contentImageInputRef.click();
    }
  }

  readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(typeof reader.result === 'string' ? reader.result : '');
    };

    reader.onerror = reject;
    reader.readAsDataURL(file);
  })

  handleContentImageChange = async (e) => {
    const input = e.target;
    const file = input.files && input.files[0] ? input.files[0] : null;

    if (!file) {
      return;
    }

    try {
      const src = await this.readFileAsDataUrl(file);
      const { contentEditorState } = this.state.form;
      const contentState = contentEditorState.getCurrentContent();
      const contentStateWithEntity = contentState.createEntity('IMAGE', 'IMMUTABLE', {
        src,
        alt: file.name
      });
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
      const nextEditorState = EditorState.set(contentEditorState, {
        currentContent: contentStateWithEntity
      });

      this.handleEditorChange(
        AtomicBlockUtils.insertAtomicBlock(nextEditorState, entityKey, ' ')
      );
    } catch (error) {
      console.error('Work content image read failed', error);
    } finally {
      input.value = '';
    }
  }

  blockRendererFn = (block) => {
    if (block.getType() !== 'atomic') {
      return null;
    }

    const contentState = this.state.form.contentEditorState.getCurrentContent();
    const entityKey = block.getEntityAt(0);

    if (!entityKey) {
      return null;
    }

    const entity = contentState.getEntity(entityKey);

    if (entity.getType() === 'IMAGE') {
      return {
        component: WorkContentImage,
        editable: false
      };
    }

    return null;
  }

  handleSubmit = (e) => {
    e.preventDefault();

    if (this.state.form.id) {
      this.submitEditWork();
      return;
    }

    this.submitCreateWork();
  }

  handleDelete = () => {
    this.submitDeleteWork();
  }

  buildWorkPayload = async () => {
    const { id, title, created, imageFile, imagePreview, contentEditorState } = this.state.form;
    const content = stateToHTML(contentEditorState.getCurrentContent(), {
      entityStyleFn: (entity) => {
        const entityType = entity.getType().toLowerCase();

        if (entityType === 'image') {
          const { src, alt } = entity.getData();

          return {
            element: 'img',
            attributes: {
              src,
              alt: alt || ''
            }
          };
        }

        return undefined;
      }
    });

    return {
      title,
      content,
      timestamp: created,
      image_base64: imageFile ? (await this.readFileAsDataUrl(imageFile)).split(',')[1] || '' : '',
      image: imageFile ? imageFile.name : imagePreview,
      id
    };
  }

  submitCreateWork = async () => {
    const token = localStorage.getItem('access_token');

    this.setState({ isSubmitting: true });

    try {
      const payload = await this.buildWorkPayload();

      console.log('Work create payload', payload);

      const response = await axios.post(`${workApiBaseUrl}/default/works`, {
        title: payload.title,
        content: payload.content,
        timestamp: payload.timestamp,
        image_base64: payload.image_base64
      }, {
        headers: {
          token
        }
      });
      console.log('Work create response', response.data);

      this.setState({
        isSubmitting: false,
        mode: 'list',
        form: {
          id: null,
          title: '',
          created: '',
          imageFile: null,
          imagePreview: '',
          contentEditorState: EditorState.createEmpty()
        }
      }, this.fetchWorkList);
    } catch (error) {
      console.error('Work create request failed', error);
      this.setState({ isSubmitting: false });
      this.showErrorToast('추가 중 오류가 발생했습니다.');
    }
  }

  submitEditWork = async () => {
    const { id } = this.state.form;
    const token = localStorage.getItem('access_token');

    this.setState({ isSubmitting: true });

    try {
      const payload = await this.buildWorkPayload();

      console.log('Work edit payload', payload);

      const response = await axios.put(`${workApiBaseUrl}/default/works/${id}`, {
        title: payload.title,
        content: payload.content,
        timestamp: payload.timestamp,
        image_base64: payload.image_base64
      }, {
        headers: {
          token
        }
      });
      console.log('Work edit response', response.data);

      this.setState({
        isSubmitting: false,
        mode: 'list',
        form: {
          id: null,
          title: '',
          created: '',
          imageFile: null,
          imagePreview: '',
          contentEditorState: EditorState.createEmpty()
        }
      }, this.fetchWorkList);
    } catch (error) {
      console.error('Work edit request failed', error);
      this.setState({ isSubmitting: false });
      this.showErrorToast('수정 중 오류가 발생했습니다.');
    }
  }

  submitDeleteWork = async () => {
    const { id } = this.state.form;
    const token = localStorage.getItem('access_token');

    if (!id) {
      return;
    }

    this.setState({ isSubmitting: true });

    try {
      console.log('Work delete payload', { id });

      const response = await axios.delete(`${workApiBaseUrl}/default/works/${id}`, {
        headers: {
          token
        }
      });
      console.log('Work delete response', response.data);

      this.setState({
        isSubmitting: false,
        mode: 'list',
        form: {
          id: null,
          title: '',
          created: '',
          imageFile: null,
          imagePreview: '',
          contentEditorState: EditorState.createEmpty()
        }
      }, this.fetchWorkList);
    } catch (error) {
      console.error('Work delete request failed', error);
      this.setState({ isSubmitting: false });
      this.showErrorToast('삭제 중 오류가 발생했습니다.');
    }
  }

  renderList() {
    const { workItems, page, pageSize, isListLoading } = this.state;
    const hasNextPage = workItems.length === pageSize;

    if (isListLoading) {
      return (
        <div className="public-content-loading">
          <div className="public-content-spinner" />
        </div>
      );
    }

    return (
      <>
        <div className="admin-news-header">
          <div>
            <div className="admin-panel-eyebrow">Editor</div>
            <h2 className="admin-panel-title">Work</h2>
          </div>
          <button type="button" className="admin-news-add-button" onClick={this.openCreateForm}>추가</button>
        </div>

        <div className="admin-news-table-wrapper">
          <table className="admin-news-table">
            <thead>
              <tr>
                <th>title</th>
                <th>created</th>
              </tr>
            </thead>
            <tbody>
              {workItems.map((item) => (
                <tr key={item.id} className="admin-news-row" onClick={() => this.openEditForm(item)}>
                  <td>{item.title}</td>
                  <td>{item.created}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="admin-news-pagination">
          <button
            type="button"
            className="admin-news-secondary-button"
            onClick={() => this.goToPage(page - 1)}
            disabled={page <= 1}
          >
            이전
          </button>
          <span className="admin-news-page-indicator">Page {page}</span>
          <button
            type="button"
            className="admin-news-secondary-button"
            onClick={() => this.goToPage(page + 1)}
            disabled={!hasNextPage}
          >
            다음
          </button>
        </div>
      </>
    );
  }

  renderForm() {
    const { form, isSubmitting, errorMessage } = this.state;
    const currentInlineStyle = form.contentEditorState.getCurrentInlineStyle();
    const selection = form.contentEditorState.getSelection();
    const currentBlock = form.contentEditorState.getCurrentContent().getBlockForKey(selection.getStartKey()) || new ContentBlock({ key: 'empty', type: 'unstyled', text: '' });
    const currentBlockType = currentBlock.getType();

    return (
      <>
        <div className="admin-news-header">
          <div>
            <div className="admin-panel-eyebrow">Editor</div>
            <h2 className="admin-panel-title">{form.id ? 'Work 수정' : 'Work 추가'}</h2>
          </div>
        </div>

        {errorMessage ? (
          <div className="admin-error-toast">{errorMessage}</div>
        ) : null}

        <form className="admin-news-form" onSubmit={this.handleSubmit}>
          <label className="admin-news-field" htmlFor="work-title">
            title
            <input
              id="work-title"
              className="admin-news-input"
              name="title"
              value={form.title}
              onChange={this.handleFormChange}
              placeholder="프로젝트 제목을 입력하세요"
            />
          </label>

          <label className="admin-news-field" htmlFor="work-created">
            timestamp
            <input
              id="work-created"
              className="admin-news-input"
              type="date"
              name="created"
              value={form.created}
              onChange={this.handleFormChange}
            />
          </label>

          <label className="admin-news-field" htmlFor="work-image">
            image
            <input
              id="work-image"
              type="file"
              accept="image/*"
              onChange={this.handleImageChange}
            />
          </label>

          {form.imagePreview ? (
            <div className="admin-news-preview">
              <img
                className="admin-news-preview-image"
                src={form.imagePreview}
                alt={form.title || 'Work preview'}
              />
              <div className="admin-news-preview-name">
                {form.imageFile ? form.imageFile.name : '기존 대표 이미지'}
              </div>
            </div>
          ) : null}

          <div className="admin-news-field">
            <span>content</span>
            <div className="admin-editor-shell">
              <div className="admin-editor-toolbar">
                {inlineControls.map((control) => (
                  <EditorToolbarButton
                    key={control.style}
                    active={currentInlineStyle.has(control.style)}
                    ariaLabel={control.ariaLabel}
                    label={control.label}
                    onMouseDown={this.toggleInlineStyle(control.style)}
                  />
                ))}
                {blockControls.map((control) => (
                  <EditorToolbarButton
                    key={control.style}
                    active={currentBlockType === control.style}
                    ariaLabel={control.ariaLabel}
                    label={control.label}
                    onMouseDown={this.toggleBlockType(control.style)}
                  />
                ))}
                <EditorToolbarButton
                  active={false}
                  ariaLabel="Insert image"
                  label={<ImageToolbarIcon />}
                  onMouseDown={this.focusContentImageInput}
                />
                <input
                  ref={(element) => {
                    this.contentImageInputRef = element;
                  }}
                  className="admin-editor-hidden-input"
                  type="file"
                  accept="image/*"
                  onChange={this.handleContentImageChange}
                />
              </div>

              <div className="admin-editor-surface" onClick={this.focusEditor}>
                <Editor
                  editorState={form.contentEditorState}
                  onChange={this.handleEditorChange}
                  handleKeyCommand={this.handleEditorKeyCommand}
                  keyBindingFn={this.mapKeyToEditorCommand}
                  placeholder="Work 상세 내용을 입력하세요."
                  blockRendererFn={this.blockRendererFn}
                  ref={(element) => {
                    this.editorRef = element;
                  }}
                />
              </div>
            </div>
          </div>

          <div className="admin-news-form-actions">
            <div>
              {form.id ? (
                <button type="button" className="admin-news-delete-button" onClick={this.handleDelete}>삭제</button>
              ) : null}
            </div>
            <div className="admin-news-form-actions-right">
              {isSubmitting ? (
                <div className="admin-inline-spinner public-content-spinner" />
              ) : null}
              <button
                type="button"
                className="admin-news-secondary-button"
                onClick={this.closeForm}
              >
                취소
              </button>
              <button
                type="submit"
                className="admin-news-add-button"
                disabled={isSubmitting}
              >
                {form.id ? '수정하기' : '추가하기'}
              </button>
            </div>
          </div>
        </form>
      </>
    );
  }

  render() {
    return (
      <div className="admin-panel-card">
        {this.state.mode === 'list' ? this.renderList() : this.renderForm()}
      </div>
    );
  }
}

export default AdminWorkManager;
